import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Radio } from "lucide-react";
import { pulseEvents, stats } from "../mock";

const ROTATE = ["ready buyers.", "in-market accounts.", "high-intent visitors.", "the next deal."];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [phase, setPhase] = useState("typing");
  const [pulseIdx, setPulseIdx] = useState(0);
  const [counter, setCounter] = useState(3204);

  useEffect(() => {
    const word = ROTATE[idx];
    let t;
    if (phase === "typing") {
      if (typed.length < word.length) {
        t = setTimeout(() => setTyped(word.slice(0, typed.length + 1)), 60);
      } else {
        t = setTimeout(() => setPhase("pausing"), 1800);
      }
    } else if (phase === "pausing") {
      t = setTimeout(() => setPhase("deleting"), 600);
    } else if (phase === "deleting") {
      if (typed.length > 0) {
        t = setTimeout(() => setTyped(word.slice(0, typed.length - 1)), 30);
      } else {
        setIdx((p) => (p + 1) % ROTATE.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [typed, phase, idx]);

  // Pulse ticker rotation + counter increment
  useEffect(() => {
    const id = setInterval(() => {
      setPulseIdx((p) => (p + 1) % pulseEvents.length);
      setCounter((c) => c + Math.floor(Math.random() * 7) + 2);
    }, 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden pt-28 pb-12 lg:pt-32 lg:pb-16">
      {/* subtle grid + plum glow background */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.32]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(30,27,75,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(30,27,75,0.06) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage:
              "radial-gradient(ellipse 70% 60% at 50% 30%, black 40%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 60% at 50% 30%, black 40%, transparent 80%)",
          }}
        />
        <div className="absolute left-1/2 top-[120px] h-[360px] w-[680px] -translate-x-1/2 rounded-full bg-[#d4af37]/15 blur-[120px]" />
        <div className="absolute left-1/3 top-[40px] h-[260px] w-[440px] rounded-full bg-[#1e1b4b]/8 blur-[100px]" />
      </div>

      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        {/* Pulse ticker — live signal strip */}
        <div className="flex justify-center">
          <div className="group inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white/90 px-3 py-1.5 text-xs shadow-sm backdrop-blur transition-all hover:border-[#d4af37]/40">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#d4af37] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#d4af37]" />
            </span>
            <span className="font-mono text-[11px] font-semibold tracking-wider text-[#1e1b4b]">
              {counter.toLocaleString()}
            </span>
            <span className="hidden text-[11px] uppercase tracking-[0.12em] text-slate-500 sm:inline">
              fresh contacts identified this hour
            </span>
            <span className="hidden h-3 w-px bg-slate-200 sm:inline-block" />
            <span className="hidden max-w-[260px] truncate text-[11px] text-slate-600 sm:inline">
              {pulseEvents[pulseIdx]}
            </span>
          </div>
        </div>

        {/* small authority eyebrow */}
        <div className="mt-5 flex justify-center">
          <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            <Compass className="h-3 w-3 text-[#d4af37]" />
            Built by 11x founders for small business
          </span>
        </div>

        <h1 className="mx-auto mt-6 max-w-5xl text-center text-[44px] font-medium leading-[1.04] tracking-[-0.025em] text-[#1e1b4b] sm:text-6xl lg:text-[78px]">
          Find <span className="text-slate-400">ready buyers</span>
          <br />
          in a sea of <span className="relative italic text-[#1e1b4b]">noise</span>.
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-center text-[17px] leading-relaxed text-slate-600">
          The data-intelligence platform that helps marketing teams reach{" "}
          <span className="font-semibold text-[#1e1b4b]">
            {typed}
            <span className="ml-0.5 inline-block h-[0.95em] w-[2px] translate-y-[0.18em] animate-pulse bg-[#d4af37]" />
          </span>
          <br className="hidden sm:block" />
          Skip the guesswork. Find people already shopping for what you sell.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/demo"
            className="group inline-flex items-center gap-2 rounded-xl bg-[#1e1b4b] px-6 py-3.5 text-sm font-medium text-white shadow-[0_12px_30px_-10px_rgba(30,27,75,0.55)] transition-all hover:bg-[#1e1b4b]/92 hover:shadow-[0_18px_40px_-12px_rgba(30,27,75,0.6)]"
          >
            Get a Demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3.5 text-sm font-medium text-[#1e1b4b] transition-all hover:border-[#1e1b4b]/30 hover:bg-slate-50"
          >
            <Radio className="h-4 w-4 text-[#d4af37]" />
            See Pricing
          </Link>
        </div>

        {/* Social-proof stat strip — anchors visitors on scale numbers above the fold */}
        <div
          className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-8"
          data-testid="hero-stats-strip"
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              data-testid={`hero-stat-${i}`}
              className="group relative text-center"
              style={{ animation: `fadeUp 700ms ease-out ${i * 120 + 200}ms both` }}
            >
              <div className="text-3xl font-medium tracking-tight text-[#1e1b4b] sm:text-4xl">
                {s.value}
              </div>
              <div className="mt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                {s.label}
              </div>
              {/* champagne underline that grows on group hover */}
              <span className="mx-auto mt-2 block h-px w-8 bg-[#d4af37]/40 transition-all duration-500 group-hover:w-16 group-hover:bg-[#d4af37]" />
            </div>
          ))}
        </div>

        {/* Trust signal pills linking to compliance pages */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-2">
          {[
            { label: "Privacy-first", to: "/privacy" },
            { label: "SOC 2 in progress", to: "/security" },
            { label: "GDPR & CCPA", to: "/compliance" },
          ].map((t) => (
            <Link
              key={t.label}
              to={t.to}
              data-testid={`hero-trust-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/60 px-3 py-1 text-[10.5px] font-medium text-slate-500 backdrop-blur transition-colors hover:border-[#d4af37]/40 hover:text-[#1e1b4b]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
              {t.label}
            </Link>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
